/**
 * Bell icon in the Topbar with an unread-count badge and a dropdown of
 * the most recent notifications.
 *
 * Polls /notifications/unread-count every 30 seconds so the badge stays
 * roughly live without websockets.
 */
import { useEffect, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Bell, Check, CheckCheck, Loader2, CheckSquare, AlertTriangle, GitBranch, Zap } from "lucide-react"
import { notificationsApi, type Notification, type NotificationKind } from "@/lib/api"
import { cn } from "@/lib/utils"
import { timeAgo } from "@shared/utils/date"

const KIND_ICON: Record<NotificationKind, typeof Bell> = {
  task_assigned: CheckSquare,
  task_due_soon: CheckSquare,
  task_overdue: AlertTriangle,
  deal_stage: GitBranch,
  contact_assigned: CheckSquare,
  automation_failed: AlertTriangle,
  system: Zap,
}
const KIND_TONE: Record<NotificationKind, string> = {
  task_assigned: "text-blue-600 bg-blue-50",
  task_due_soon: "text-amber-600 bg-amber-50",
  task_overdue: "text-red-600 bg-red-50",
  deal_stage: "text-emerald-600 bg-emerald-50",
  contact_assigned: "text-blue-600 bg-blue-50",
  automation_failed: "text-red-600 bg-red-50",
  system: "text-muted-foreground bg-muted",
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()
  const navigate = useNavigate()

  // Poll unread count every 30s
  const count = useQuery({
    queryKey: ["notifications-count"],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 30_000,
  })
  const unread = count.data?.unread ?? 0

  // Fetch the list only when dropdown open
  const list = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () => notificationsApi.list({ limit: 20 }),
    enabled: open,
    refetchOnWindowFocus: true,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications-count"] })
      qc.invalidateQueries({ queryKey: ["notifications"] })
    },
  })
  const markAll = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications-count"] })
      qc.invalidateQueries({ queryKey: ["notifications"] })
    },
  })

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  const openItem = (n: Notification) => {
    if (!n.readAt) markRead.mutate(n.id)
    if (n.href) navigate(n.href)
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-muted-foreground hover:bg-accent hover:text-foreground relative rounded-md p-2"
        title={unread > 0 ? `${unread} unread notification${unread === 1 ? "" : "s"}` : "Notifications"}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="bg-rc-blue-600 pointer-events-none absolute -right-0.5 -top-0.5 grid min-w-[18px] h-[18px] place-items-center rounded-full px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="border-border absolute right-0 top-full z-50 mt-2 w-96 rounded-lg border bg-white shadow-lg">
          <div className="border-border flex items-center justify-between border-b px-4 py-2.5">
            <div className="text-sm font-semibold text-foreground">
              Notifications
              {unread > 0 && (
                <span className="text-muted-foreground ml-1.5 text-xs font-normal">
                  ({unread} unread)
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                className="text-rc-blue-600 hover:text-rc-blue-700 flex items-center gap-1 text-xs font-medium"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {list.isLoading ? (
              <div className="grid place-items-center p-8">
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
              </div>
            ) : !list.data?.items.length ? (
              <div className="grid place-items-center p-8 text-center">
                <Bell className="text-muted-foreground/50 mb-2 h-6 w-6" />
                <p className="text-muted-foreground text-sm">You're all caught up</p>
              </div>
            ) : (
              <ul className="divide-border divide-y">
                {list.data.items.map((n) => {
                  const Icon = KIND_ICON[n.kind] ?? Bell
                  const tone = KIND_TONE[n.kind] ?? "text-muted-foreground bg-muted"
                  const unreadItem = !n.readAt
                  return (
                    <li
                      key={n.id}
                      onClick={() => openItem(n)}
                      className={cn(
                        "hover:bg-rc-grey-50 flex cursor-pointer items-start gap-3 px-4 py-3",
                        unreadItem && "bg-rc-blue-50/40"
                      )}
                    >
                      <div className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-full", tone)}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={cn("text-sm", unreadItem ? "font-semibold text-foreground" : "text-foreground")}>
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
                            {n.body}
                          </p>
                        )}
                        <p className="text-muted-foreground mt-1 text-[11px]">
                          {timeAgo(n.createdAt)}
                        </p>
                      </div>
                      {unreadItem && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            markRead.mutate(n.id)
                          }}
                          className="text-muted-foreground hover:text-foreground shrink-0"
                          title="Mark read"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="border-border border-t px-4 py-2 text-center">
            <Link
              to="/tasks"
              onClick={() => setOpen(false)}
              className="text-rc-blue-600 hover:text-rc-blue-700 text-xs font-medium"
            >
              View all tasks →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
